import { useState } from 'react';
import { Play, Instagram } from 'lucide-react';
import type { CreatorVideo } from '@/config/creatorVideos';

interface CreatorVideoCardProps {
  video: CreatorVideo;
  isPlaying: boolean;
  onPlay: (id: string) => void;
  /** Sobrescreve a largura do card (ex.: grade 2/4 colunas na home v2). */
  className?: string;
}

/**
 * Card de vídeo UGC compartilhado entre home e PDP.
 *
 * Performance: NÃO renderiza <video> de início — mostra a capa (img lazy)
 * com botão de play. O <video> só entra no DOM ao clicar, com autoPlay
 * pós-interação. Assim a página não baixa nenhum vídeo sem ação do usuário.
 *
 * O crédito à creator (@handle) é obrigatório — nunca exibir sem ele.
 */
export const CreatorVideoCard = ({ video, isPlaying, onPlay, className }: CreatorVideoCardProps) => {
  return (
    <div className={className ?? "flex-none w-[70vw] max-w-[300px] sm:w-[calc(50%-8px)] sm:max-w-none lg:w-full min-w-0"}>
      <div className="relative aspect-[9/16] rounded-xl overflow-hidden border border-border bg-muted">
        {isPlaying ? (
          <video
            src={video.video}
            poster={video.poster}
            autoPlay
            controls
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <>
            <img
              src={video.poster}
              alt={`Capa do vídeo de ${video.creator}: ${video.legenda}`}
              width={720}
              height={1280}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => onPlay(video.id)}
              aria-label={`Assistir vídeo de ${video.creator}`}
              className="absolute inset-0 w-full h-full flex items-center justify-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <span className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
              </span>
            </button>
          </>
        )}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-foreground/70 to-transparent p-3 pointer-events-none">
          <p className="text-sm font-medium text-background">{video.creator}</p>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{video.legenda}</p>
    </div>
  );
};

interface CreatorVideosSectionProps {
  videos: CreatorVideo[];
  /** Compacto na PDP: sem título grande, sem CTA de Instagram. */
  compact?: boolean;
}

export const CreatorVideosSection = ({ videos, compact = false }: CreatorVideosSectionProps) => {
  const [playingId, setPlayingId] = useState<string | null>(null);

  if (videos.length === 0) return null;

  const handlePlay = (id: string) => {
    // Só um vídeo toca por vez: abrir um novo devolve o anterior à capa.
    setPlayingId((current) => (current === id ? null : id));
  };

  return (
    <section className={compact ? 'py-8' : 'py-14 md:py-28 bg-background'}>
      <div className={compact ? '' : 'container mx-auto px-4'}>
        <div className={compact ? 'mb-4' : 'text-center mb-8 md:mb-12'}>
          <h2 className={compact ? 'text-xl font-semibold text-foreground' : 'text-2xl md:text-4xl font-bold text-foreground mb-4'}>
            Quem usa, mostra
          </h2>
          {!compact && (
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
              Rotinas reais de quem cuida da pele sensível todos os dias.
            </p>
          )}
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory lg:grid lg:grid-cols-3 lg:overflow-visible lg:pb-0">
          {videos.map((video) => (
            <CreatorVideoCard
              key={video.id}
              video={video}
              isPlaying={playingId === video.id}
              onPlay={handlePlay}
            />
          ))}
        </div>

        {!compact && (
          <div className="text-center mt-8">
            <a
              href="https://www.instagram.com/nat.hof.care/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Instagram className="w-4 h-4" />
              Siga @nat.hof.care
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
